import { Box, Paper, Rating, Skeleton, Stack, Typography } from "@mui/material";
import { renderFingerprintImage } from "Components/Applicants/ApplicantHelpers";
import { ShImageWithIconBox } from "Components/Applicants/ApplicantProfile/Applicant.styled";
import { MatchTypes, SkillMap } from "Components/Applicants/ApplicantsConstants";
import { ApplicantRating } from "Components/Applicants/ApplicantsList/ApplicantRating";
import { CandidateEvalImgCntnr, FingerprintImageContainer } from "Components/Applicants/ApplicantsList/ApplicantsList.styled";
import { StagesToggler } from "Components/Applicants/ApplicantsList/StagesToggler";
import { Stage } from "Components/Applicants/ApplicantsModel";
import { DistortionIcon, FitIcon, StrongFitIcon, WeakFitIcon } from "assets/Icons";
import { useAppDispatch, useAppSelector } from "helpers/hooks";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { ApiState } from "shared/SharedModels";
import { UnderlinedText } from "shared/SharedStyles/ShDataDisplay";
import { ShAlert } from "shared/SharedStyles/ShFeedback";
import { ShContainer } from "shared/SharedStyles/ShLayouts";
import { DefaultAPIErrorMsg } from "shared/constants";
import { getFullFingerPrintImage } from "store/slices/employer/applicants/applicant-profile-slice";
import { changeApplicantStage, fetchApplicantSummary } from "store/slices/employer/applicants/applicants-list-slice";

export const ApplicantProfile = () => {

    const { jobId, applicantId } = useParams();
    const dispatch = useAppDispatch();
    const { applicantSummary, getApplicantSummaryStatus, getApplicantSummaryResponse,
        currentApplicantProfile } = useAppSelector((state) => state.employer.applicantsList);
    const { getFullFingerPrintImageStatus, getFullFingerPrintImageResponse,
        fullFingerPrintImage } = useAppSelector(state => state.candidate.applicantProfile);

    useEffect(() => {
        if (currentApplicantProfile) {
            if (applicantId && applicantSummary === undefined) {
                dispatch(fetchApplicantSummary({ candidateEmployerJobId: parseInt(applicantId), jobId: parseInt(jobId ?? '') }));
            }
        }
    }, [applicantId, applicantSummary, currentApplicantProfile, dispatch, jobId]);

    // Load fingerprint image once summary is loaded.
    useEffect(() => {
        if (applicantId) {
            dispatch(getFullFingerPrintImage({ applicantId: parseInt(applicantId) }));
        }
    }, [applicantId, dispatch]);

    // Append the svg into parent element once its loaded. 
    useEffect(() => {
        if (getFullFingerPrintImageStatus === 'success') {
            if (fullFingerPrintImage) {
                renderFingerprintImage(fullFingerPrintImage, applicantId +
                    'full_fingerprint_svg_parent');
            }
        }
    }, [applicantId, fullFingerPrintImage, getFullFingerPrintImageStatus]);

    const changeStage = (stage: Lowercase<Stage>, candidateEmployerJobId: number) => {
        dispatch(changeApplicantStage({ applicantId: candidateEmployerJobId, payload: { stage: stage } }));
    };

    const showFailureAlerts = (apiState?: ApiState, msg?: string) => {
        switch (apiState) {
            case 'failed':
                return (<>
                    <Box marginBottom={2} width='100%'>
                        <ShAlert severity="error">{msg ?? DefaultAPIErrorMsg}</ShAlert>
                    </Box>
                </>);
            default:
                break;
        }
    };

    return (<>
        <ShContainer maxWidth='md' disableGutters>
            <Stack rowGap={2} width='fit-content'>
                {currentApplicantProfile &&
                    <Stack direction='row' columnGap={3} flexWrap='wrap'>
                        <Stack rowGap={1}>
                            <UnderlinedText variant="subtitle2" fontWeight={600}>
                                Name
                            </UnderlinedText>
                            <Typography variant="body2">
                                {`${currentApplicantProfile?.firstName} ${currentApplicantProfile?.lastName}`}
                            </Typography>
                        </Stack>
                        <Stack rowGap={1}>
                            <UnderlinedText variant="subtitle2" fontWeight={600}>
                                Email Address
                            </UnderlinedText>
                            <Typography variant="body2">
                                {currentApplicantProfile?.email}
                            </Typography>
                        </Stack>
                        <Stack rowGap={1}>
                            <UnderlinedText variant="subtitle2" fontWeight={600}>
                                Rating
                            </UnderlinedText>
                            <Typography variant="body2">
                                <ApplicantRating className='dsk-only-inline-flex' applicant={currentApplicantProfile} />
                            </Typography>
                        </Stack>
                        <Stack rowGap={1}>
                            <UnderlinedText variant="subtitle2" fontWeight={600}>
                                Stage
                            </UnderlinedText>
                            <StagesToggler className='dsk-only-inline-flex' applicant={currentApplicantProfile} changeStage={changeStage} />
                        </Stack>
                    </Stack>}
                {showFailureAlerts(getApplicantSummaryStatus, getApplicantSummaryResponse)}
                <Paper variant="outlined">
                    <Stack direction='row' alignItems='center' columnGap={3} padding={2}>
                        <ShImageWithIconBox height={{ xs: '100px', sm: '100px', md: '125px', lg: '150px' }}
                            width={{ xs: '100px', sm: '100px', md: '125px', lg: '150px' }}>
                            {currentApplicantProfile?.matchType === MatchTypes.strong ? <StrongFitIcon /> :
                                currentApplicantProfile?.matchType === MatchTypes.distortion ? <DistortionIcon /> :
                                    currentApplicantProfile?.matchType === MatchTypes.match ? <FitIcon /> : <WeakFitIcon />}
                        </ShImageWithIconBox>
                        <Stack>
                            <UnderlinedText variant="body1" fontWeight={600}>
                                Skills Summary
                            </UnderlinedText>
                            <Stack height='100%' justifyContent='center' rowGap={1}>
                                <Stack>
                                    <Typography variant='subtitle2' fontWeight='bold' gutterBottom>Education & Qualifications</Typography>
                                    {applicantSummary?.educationQualificationRequirements?.map((eq, indx) =>
                                        (<Typography key={indx} variant='body2'>{eq.name}</Typography>))}
                                </Stack>
                                <Stack>
                                    <Typography variant='subtitle2' fontWeight='bold' gutterBottom>Skills</Typography>
                                    {applicantSummary?.skillRequirements?.map((skl, indx) =>
                                    (<Stack flexDirection='row' key={indx}>
                                        <Rating id={applicantId + ''} size='small'
                                            value={SkillMap[skl.skillRating] ?? 0} readOnly />
                                        <Typography variant='body2'>{skl.name}</Typography></Stack>))}
                                </Stack>
                            </Stack>
                        </Stack>
                    </Stack>
                </Paper>
                {showFailureAlerts(getFullFingerPrintImageStatus, getFullFingerPrintImageResponse)}
                <Stack rowGap={2}>
                    <UnderlinedText variant="body1" fontWeight={600}>
                        Job Fit Details
                    </UnderlinedText>
                    <Typography variant="subtitle2">
                        The JobFingerprint™ below shows whether name's scores meet, exceed or are below the target areas.
                        Interview questions have been created for you to probe on the watch out areas,
                        where the applicant may have a score less than the expectations for this role.
                    </Typography>
                    <CandidateEvalImgCntnr item xs={12} sm={12} md={6} lg={5} xl={4}>
                        {(getFullFingerPrintImageStatus && getFullFingerPrintImageResponse) &&
                            showFailureAlerts(getFullFingerPrintImageStatus, getFullFingerPrintImageResponse)}
                        {getFullFingerPrintImageStatus === 'pending' ?
                            <Skeleton width='100%' height='100%' animation='wave' /> :
                            <>
                                {/* /Placeholder/Parent block to which the fingerprint SVG will be appended. */}
                                <FingerprintImageContainer
                                    id={applicantId + 'full_fingerprint_svg_parent'}>
                                </FingerprintImageContainer>
                                <img alt="Legend"
                                    src="https://app.smoothhiring.com/assets/candidate_hiring_guide/legend-dc97b8db258df1071c04609519e107c3.png"></img>
                            </>}
                    </CandidateEvalImgCntnr>
                </Stack>
            </Stack>
        </ShContainer>
    </>);
};